/**
 * <s-delivery-estimate> — Estimated delivery dates
 * The server renders a first estimate; this element recalculates it in the
 * visitor's browser so cached pages always show dates counted from today.
 *
 * Attributes (data-*):
 *   min-days / max-days — total days to add for the start and end dates
 *   skip-weekends       — present when Saturday and Sunday are not counted
 *   format              — 'long' (October 24), 'short' (Oct 24) or 'weekday' (Sat, Oct 24)
 *
 * Targets: [data-delivery-start], [data-delivery-end]
 */
class SDeliveryEstimate extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#render();
    // Refresh when a tab left open overnight becomes visible again.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.#render();
    }, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #render() {
    const skipWeekends = this.hasAttribute('data-skip-weekends');
    const start = this.#addDays(Number(this.dataset.minDays) || 0, skipWeekends);
    const end = this.#addDays(Number(this.dataset.maxDays) || 0, skipWeekends);
    const formatter = new Intl.DateTimeFormat(document.documentElement.lang || 'en', this.#formatOptions());
    const startTarget = this.querySelector('[data-delivery-start]');
    const endTarget = this.querySelector('[data-delivery-end]');
    if (startTarget) startTarget.textContent = formatter.format(start);
    if (endTarget) endTarget.textContent = formatter.format(end);
  }

  #formatOptions() {
    switch (this.dataset.format) {
      case 'short': return { month: 'short', day: 'numeric' };
      case 'weekday': return { weekday: 'short', month: 'short', day: 'numeric' };
      default: return { month: 'long', day: 'numeric' };
    }
  }

  #addDays(count, skipWeekends) {
    const date = new Date();
    date.setHours(12, 0, 0, 0);
    let added = 0;
    while (added < count) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (skipWeekends && (day === 0 || day === 6)) continue;
      added += 1;
    }
    return date;
  }
}

if (!customElements.get('s-delivery-estimate')) {
  customElements.define('s-delivery-estimate', SDeliveryEstimate);
}
